import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  IconButton,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Tooltip,
  CircularProgress,
  Alert,
  InputAdornment,
  Paper,
  Divider,
} from "@mui/material";
import {
  Delete,
  ShoppingCart,
  Visibility,
  Edit,
  Refresh,
  Search,
  FilterList,
  Favorite as FavoriteIcon,
  PictureAsPdf,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { jsPDF } from "jspdf";
import { useCompanyInfo } from "../../../hooks/useCompanyInfo";
import AddToWishlistDialog from "../components/AddToWishlistDialog";
import {
  getWishlistSummary,
  removeFromWishlist,
  updateWishlistItem,
  bulkDeleteWishlistItems,
  bulkUpdatePriority,
} from "../services/wishListService";

// ── Code 128B patterns (symbol values 0–105; STOP handled separately) ─────────
const CODE128_CHARS = [
  '11011001100','11001101100','11001100110','10010011000','10010001100',
  '10001001100','10011001000','10011000100','10001100100','11001001000',
  '11001000100','11000100100','10110011100','10011011100','10011001110',
  '10111001100','10011101100','10011100110','11001110010','11001011100',
  '11001001110','11011100100','11001110100','11101101110','11101001100',
  '11100101100','11100100110','11101100100','11100110100','11100110010',
  '11011011000','11011000110','11000110110','10100011000','10001011000',
  '10001000110','10110001000','10001101000','10001100010','11010001000',
  '11000101000','11000100010','10110111000','10110001110','10001101110',
  '10111011000','10111000110','10001110110','11101110110','11010001110',
  '11000101110','11011101000','11011100010','11011101110','11101011000',
  '11101000110','11100010110','11101101000','11101100010','11100011010',
  '11101111010','11001000010','11110001010','10100110000','10100001100',
  '10010110000','10010000110','10000101100','10000100110','10110010000',
  '10110000100','10011010000','10011000010','10000110100','10000110010',
  '11000010010','11001010000','11110111010','11000010100','10001111010',
  '10100111100','10010111100','10010011110','10111100100','10011110100',
  '10011110010','11110100100','11110010100','11110010010','11011011110',
  '11011110110','11110110110','10101111000','10100011110','10001011110',
  '10111101000','10111100010','11110101000','11110100010','10111011110',
  '10111101110','11101011110','11110101110',
  '11010000100','11010010000','11010011100', // 103=START A, 104=START B, 105=START C
];
const CODE128_STOP = '1100011101011'; // 13 modules

/** Draw a Code 128B barcode onto a jsPDF document.  Returns immediately if text is empty or column too narrow. */
function drawCode128B(doc, text, x, y, colWidth, barHeight) {
  const safe = (text || '').split('')
    .filter(c => { const n = c.charCodeAt(0); return n >= 32 && n <= 126; })
    .join('').slice(0, 40);
  if (!safe) return;
  const START_B = 104;
  let checksum = START_B;
  const codes = [START_B];
  for (let i = 0; i < safe.length; i++) {
    const v = safe.charCodeAt(i) - 32;
    codes.push(v);
    checksum += v * (i + 1);
  }
  codes.push(checksum % 103);
  // modules: each code=11, STOP=13, termination bar=2
  const totalModules = codes.length * 11 + CODE128_STOP.length + 2;
  const bw = (colWidth - 2) / totalModules;
  if (bw < 0.08) return;
  let xp = x + 1;
  doc.setFillColor(0, 0, 0);
  codes.forEach(code => {
    const pat = CODE128_CHARS[code] || '';
    for (let i = 0; i < pat.length; i++) {
      if (pat[i] === '1') doc.rect(xp, y, bw, barHeight, 'F');
      xp += bw;
    }
  });
  for (let i = 0; i < CODE128_STOP.length; i++) {
    if (CODE128_STOP[i] === '1') doc.rect(xp, y, bw, barHeight, 'F');
    xp += bw;
  }
  doc.rect(xp, y, bw * 2, barHeight, 'F'); // termination bar
}

const PRIORITY_COLOR = { High: "error", Medium: "warning", Low: "success" };

const getWishlistProductName = (item) => {
  if (item?.ProductId) {
    return item?.ProductName?.trim() || "-";
  }

  return item?.CustomProductName?.trim() || "-";
};

// Reusable stat card
const StatCard = ({ label, value, color }) => (
  <Card sx={{ flex: 1, minWidth: 130 }}>
    <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 700, color }}>
        {value}
      </Typography>
    </CardContent>
  </Card>
);

// Main component
const WishlistPage = () => {
  const { t } = useTranslation();
  const { companyInfo } = useCompanyInfo();

  // Data
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("All");
  const [supplierFilter, setSupplierFilter] = useState("");

  // Selection
  const [selectedIds, setSelectedIds] = useState([]);

  // Dialogs
  const [deleteDialog, setDeleteDialog] = useState({ open: false, item: null });
  const [editDialog, setEditDialog] = useState({ open: false, item: null, loading: false });
  const [bulkPriorityDialog, setBulkPriorityDialog] = useState({ open: false, value: "Medium" });
  const [addDialog, setAddDialog] = useState({ open: false });
  const [clearListDialog, setClearListDialog] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);  
  const [confirmPdfDialog, setConfirmPdfDialog] = useState(false);
  const [pdfGenerating,    setPdfGenerating]    = useState(false);

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await getWishlistSummary();
      const data = res?.payload ?? res?.data ?? res ?? {};
      setSummary(data);
      setItems(data?.Items ?? data?.items ?? []);
    } catch {
      setError(t("failed_to_load_wishlist"));
      toast.error(t("failed_to_load_wishlist"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Derived data
  const suppliers = useMemo(() => {
    const map = new Map();
    items.forEach((i) => {
      if (i.SupplierId && i.SupplierName) map.set(i.SupplierId, i.SupplierName);
    });
    return [...map.entries()].map(([id, name]) => ({ id, name }));
  }, [items]);

  const filteredItems = useMemo(
    () =>
      items.filter((item) => {
        const productName = getWishlistProductName(item).toLowerCase();
        const sku = (item.ProductSKU || "").toLowerCase();
        const notes = (item.Notes || "").toLowerCase();

        if (priorityFilter !== "All" && item.Priority !== priorityFilter) return false;
        if (supplierFilter && item.SupplierId !== Number(supplierFilter)) return false;
        if (searchTerm) {
          const term = searchTerm.toLowerCase();
          if (!productName.includes(term) && !sku.includes(term) && !notes.includes(term)) {
            return false;
          }
        }
        return true;
      }),
    [items, priorityFilter, supplierFilter, searchTerm]
  );

  const filteredTotal = useMemo(
    () => filteredItems.reduce((s, i) => s + (i.TotalEstimatedValue || 0), 0),
    [filteredItems]
  );

  const allSelected =
    filteredItems.length > 0 && filteredItems.every((i) => selectedIds.includes(i.Id));
  const someSelected = selectedIds.length > 0;

  // Generate PDF — sorted by category, visual barcodes, images, notes, correct store info
  const generatePdf = useCallback(async () => {
    setPdfGenerating(true);
    try {
      // ── 2. Sort items by category ─────────────────────────────────────────
      const sorted = [...filteredItems].sort((a, b) =>
        (a.ProductCategoryName || '').localeCompare(b.ProductCategoryName || '')
      );

      // ── 3. Normalise store info (Redux stores lowercase keys) ────────────
      const ci = companyInfo || {};
      const storeName    = ci.name    || ci.CompanyName || ci.Name    || '';
      const storeAddress = ci.address || ci.Address     || ci.Street  || '';
      const storePhone   = ci.phone   || ci.Phone       || ci.PhoneNumber || '';
      const storeEmail   = ci.email   || ci.Email       || '';
      const storeVAT     = ci.vat     || ci.BTWNumber   || ci.VATNumber   || '';

      // ── 4. jsPDF setup ────────────────────────────────────────────────────
      const doc = new jsPDF({ unit: 'mm', format: 'a4' });
      const PW = 210, PH = 297, M = 12, CW = PW - 2 * M;
      let y = M;
      const today = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

      // ── 5. Page header: store info (left) + title (right) ─────────────────
      const storeLines = [
        storeAddress,
        storePhone ? `Tel: ${storePhone}` : '',
        storeEmail,
        storeVAT   ? `VAT: ${storeVAT}`  : '',
      ].filter(Boolean);
      const storeBoxH = 8 + storeLines.length * 4.2 + 3;

      doc.setFillColor(245, 241, 255);
      doc.rect(M, y, 88, storeBoxH, 'F');
      doc.setDrawColor(200, 180, 240);
      doc.rect(M, y, 88, storeBoxH, 'S');

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(13);
      doc.setTextColor(20, 20, 20);
      doc.text(storeName || t("your_store"), M + 3, y + 6.5);
      let sY = y + 11;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8.5);
      doc.setTextColor(55, 55, 55);
      storeLines.forEach(line => { doc.text(line, M + 3, sY); sY += 4.2; });

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(22);
      doc.setTextColor(88, 28, 135);
      doc.text(t("purchase_order"), PW - M, y + 8, { align: 'right' });
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(100, 100, 100);
      doc.text(`${t("date")}: ${today}`, PW - M, y + 15, { align: 'right' });
      doc.text(`${t("reference")}: PO-${Date.now().toString().slice(-8)}`, PW - M, y + 20, { align: 'right' });
      if (supplierFilter) {
        const sName = suppliers.find(s => s.id === Number(supplierFilter))?.name || '';
        doc.text(`${t("supplier")}: ${sName}`, PW - M, y + 25, { align: 'right' });
      }

      y = Math.max(y + storeBoxH, y + 32) + 5;
      doc.setDrawColor(88, 28, 135);
      doc.setLineWidth(0.4);
      doc.line(M, y, PW - M, y);
      doc.setLineWidth(0.2);
      y += 5;

      // ── 6. Column definitions (total = CW = 186 mm) ────────────────────────
      const cols = [
        { key: 'no',       label: '#',        w: 6,  align: 'center' },
        { key: 'product',  label: t("product"),  w: 50, align: 'left'   },
        { key: 'category', label: t("category"), w: 25, align: 'left'   },
        { key: 'barcode',  label: t("barcode"),  w: 45, align: 'left'   },
        { key: 'sku',      label: t("sku"),      w: 32, align: 'left'   },
        { key: 'qty',      label: t("qty"),      w: 12, align: 'center' },
        { key: 'priority', label: t("priority"), w: 16, align: 'center' },
      ]; // 6+50+25+45+32+12+16 = 186 = CW ✓
      let cx = M;
      cols.forEach(c => { c.x = cx; cx += c.w; });
      const gc = key => cols.find(c => c.key === key);

      // Row layout constants
      const BAR_H     = 9;
      const BAR_TXT_H = 3.5;
      const BAR_TOTAL = BAR_H + BAR_TXT_H + 1;
      const ROW_PAD   = 2;
      const NAME_LH   = 4.2;
      const NOTE_LH   = 3.5;
      const MIN_ROW_H = Math.max(BAR_TOTAL + ROW_PAD * 2, 14);
      const HEAD_H    = 7.5;

      // ── 7. Table header drawer (re-used on each page) ─────────────────────
      const drawTableHeader = () => {
        doc.setFillColor(88, 28, 135);
        doc.rect(M, y, CW, HEAD_H, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8);
        doc.setTextColor(255, 255, 255);
        cols.forEach(col => {
          const tx = col.align === 'center' ? col.x + col.w / 2
                   : col.align === 'right'  ? col.x + col.w - 1.5
                   :                          col.x + 2;
          doc.text(col.label, tx, y + 5, { align: col.align });
        });
        y += HEAD_H;
      };
      drawTableHeader();

      // ── 8. Render rows grouped by category ──────────────────────────────
      const grouped = new Map();
      sorted.forEach(item => {
        const cat = item.ProductCategoryName || 'Uncategorized';
        if (!grouped.has(cat)) grouped.set(cat, []);
        grouped.get(cat).push(item);
      });

      let rowIdx = 0;
      for (const [, catItems] of grouped) {
        for (const item of catItems) {
          // Calculate row height based on content
          const prodCol  = gc('product');
          const textColW = prodCol.w - 3;

          doc.setFontSize(8);
          const productName = getWishlistProductName(item);
          const nameLines = doc.splitTextToSize(productName, textColW);
          doc.setFontSize(7);
          const noteLines = item.Notes ? doc.splitTextToSize(item.Notes, textColW) : [];
          const contentH  = nameLines.length * NAME_LH + (noteLines.length ? noteLines.length * NOTE_LH + 2 : 0);
          const rowH = Math.max(MIN_ROW_H, contentH + ROW_PAD * 2);

          // Page break
          if (y + rowH > PH - M - 12) { doc.addPage(); y = M; drawTableHeader(); rowIdx = 0; }

          // Alternating background
          if (rowIdx % 2 === 0) {
            doc.setFillColor(250, 248, 255);
            doc.rect(M, y, CW, rowH, 'F');
          }
          doc.setDrawColor(220, 215, 230);
          doc.line(M, y + rowH, M + CW, y + rowH);

          const midY   = y + rowH / 2;
          const topPad = y + ROW_PAD;

          // # column
          const noCol = gc('no');
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(8);
          doc.setTextColor(150, 135, 170);
          doc.text(String(rowIdx + 1), noCol.x + noCol.w / 2, midY + 1.5, { align: 'center' });

          // Product column: name + italic notes
          const textX = prodCol.x + 2;

          doc.setFont('helvetica', 'bold');
          doc.setFontSize(8);
          doc.setTextColor(20, 20, 20);
          let lineY = topPad + NAME_LH;
          nameLines.forEach(line => { doc.text(line, textX, lineY); lineY += NAME_LH; });

          if (noteLines.length > 0) {
            lineY += 1.5;
            doc.setFont('helvetica', 'italic');
            doc.setFontSize(7);
            doc.setTextColor(110, 85, 160); // purple-tinted to differentiate from name
            noteLines.forEach(line => { doc.text(line, textX, lineY); lineY += NOTE_LH; });
          }

          // Category column (text only — already sorted/grouped)
          const catCol  = gc('category');
          const cLines  = doc.splitTextToSize(item.ProductCategoryName || '-', catCol.w - 2);
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(7.5);
          doc.setTextColor(60, 55, 75);
          cLines.slice(0, 3).forEach((line, li) => {
            doc.text(line, catCol.x + 1.5, topPad + NAME_LH + li * NOTE_LH);
          });

          // Barcode column — Code 128B visual
          const barcodeCol  = gc('barcode');
          const barcodeText = String(item.Barcode || item.BarCode || '').trim();
          if (barcodeText) {
            const bY = midY - (BAR_H + BAR_TXT_H) / 2;
            drawCode128B(doc, barcodeText, barcodeCol.x, bY, barcodeCol.w, BAR_H);
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(6);
            doc.setTextColor(30, 30, 30);
            doc.text(
              barcodeText.length > 28 ? barcodeText.slice(0, 27) + '\u2026' : barcodeText,
              barcodeCol.x + barcodeCol.w / 2, bY + BAR_H + BAR_TXT_H,
              { align: 'center' }
            );
          } else {
            doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5); doc.setTextColor(195, 190, 205);
            doc.text('\u2014', barcodeCol.x + barcodeCol.w / 2, midY + 1.5, { align: 'center' });
          }

          // SKU column — Code 128B visual
          const skuCol  = gc('sku');
          const skuText = String(item.ProductSKU || item.SKU || '').trim();
          if (skuText) {
            const bY = midY - (BAR_H + BAR_TXT_H) / 2;
            drawCode128B(doc, skuText, skuCol.x, bY, skuCol.w, BAR_H);
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(6);
            doc.setTextColor(30, 30, 30);
            doc.text(
              skuText.length > 20 ? skuText.slice(0, 19) + '\u2026' : skuText,
              skuCol.x + skuCol.w / 2, bY + BAR_H + BAR_TXT_H,
              { align: 'center' }
            );
          } else {
            doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5); doc.setTextColor(195, 190, 205);
            doc.text('\u2014', skuCol.x + skuCol.w / 2, midY + 1.5, { align: 'center' });
          }

          // Qty column
          const qtyCol = gc('qty');
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(10);
          doc.setTextColor(20, 20, 20);
          doc.text(String(item.Quantity), qtyCol.x + qtyCol.w / 2, midY + 1.5, { align: 'center' });

          // Priority badge
          const priCol = gc('priority');
          const priPalette = { High: [220, 38, 38], Medium: [217, 119, 6], Low: [22, 163, 74] };
          const [r, g, b] = priPalette[item.Priority] || [140, 140, 140];
          const bW = priCol.w - 3, bH = 5.5;
          const bX = priCol.x + 1.5, bYp = midY - bH / 2;
          doc.setFillColor(r, g, b);
          doc.rect(bX, bYp, bW, bH, 'F');
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(7);
          doc.setTextColor(255, 255, 255);
          doc.text(item.Priority || '-', bX + bW / 2, bYp + 3.7, { align: 'center' });

          y += rowH;
          rowIdx++;
        }
      }

      // ── 9. Summary footer ──────────────────────────────────────────────────
      if (y + 16 > PH - M) { doc.addPage(); y = M + 5; }
      y += 4;
      doc.setDrawColor(88, 28, 135);
      doc.setLineWidth(0.4);
      doc.line(M, y, M + CW, y);
      doc.setLineWidth(0.2);
      y += 6;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9.5);
      doc.setTextColor(30, 30, 30);
      doc.text(`${t("total_items")}: ${sorted.length}`, M, y);
      doc.text(`${t("estimated_total")}: \u20AC${filteredTotal.toFixed(2)}`, M + CW, y, { align: 'right' });

      // ── 10. Page numbers ───────────────────────────────────────────────────
      const totalPages = doc.getNumberOfPages();
      for (let p = 1; p <= totalPages; p++) {
        doc.setPage(p);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7);
        doc.setTextColor(170, 170, 170);
        doc.text(
          `${t("page")} ${p} ${t("of")} ${totalPages}  \u2014  ${t("generated")} ${today}`,
          PW / 2, PH - 5, { align: 'center' }
        );
      }

      // ── 11. Save ───────────────────────────────────────────────────────────
      const sLabel = supplierFilter
        ? (suppliers.find(s => s.id === Number(supplierFilter))?.name || 'supplier').replace(/\s+/g, '-')
        : 'all-suppliers';
      doc.save(`purchase-order-${sLabel}-${today.replace(/ /g, '-')}.pdf`);
      toast.success(t("pdf_exported_successfully"));
    } catch {
      toast.error(t("failed_to_generate_pdf"));
    } finally {
      setPdfGenerating(false);
    }
  }, [filteredItems, filteredTotal, supplierFilter, suppliers, companyInfo]);

  // Export PDF handler - show confirmation dialog
  const handleExportPdf = useCallback(() => {
    if (filteredItems.length === 0) {
      toast.warning(t("no_items_to_export"));
      return;
    }
    setConfirmPdfDialog(true);
  }, [filteredItems]);

  // Selection helpers
  const toggleAll = () =>
    setSelectedIds(allSelected ? [] : filteredItems.map((i) => i.Id));

  const toggleOne = (id) =>
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  // Delete single
  const handleDeleteConfirm = async () => {
    const { item } = deleteDialog;
    try {
      await removeFromWishlist(item.Id);
      toast.success(t("removed_from_wishlist", { item: getWishlistProductName(item) }));
      setDeleteDialog({ open: false, item: null });
      setSelectedIds((prev) => prev.filter((x) => x !== item.Id));
      fetchData();
    } catch {
      toast.error(t("failed_to_remove_item"));
    }
  };

  // Bulk delete
  const handleBulkDelete = async () => {
    try {
      setBulkActionLoading(true);
      await bulkDeleteWishlistItems(selectedIds);
      toast.success(t("items_removed", { count: selectedIds.length }));
      setSelectedIds([]);
      fetchData();
    } catch {
      toast.error(t("bulk_delete_failed"));
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Clear entire list
  const handleClearList = async () => {
    try {
      setBulkActionLoading(true);
      const allIds = items.map(item => item.Id);
      await bulkDeleteWishlistItems(allIds);
      toast.success(t("wishlist_cleared") || "Wishlist cleared successfully");
      setSelectedIds([]);
      setClearListDialog(false);
      fetchData();
    } catch {
      toast.error(t("failed_to_clear_wishlist") || "Failed to clear wishlist");
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Bulk priority update
  const handleBulkUpdatePriority = async () => {
    try {
      setBulkActionLoading(true);
      await bulkUpdatePriority({
        WishlistItemIds: selectedIds,
        Action: "UpdatePriority",
        NewPriority: bulkPriorityDialog.value,
      });
      toast.success(t("priority_set_for_items", { priority: bulkPriorityDialog.value, count: selectedIds.length }));
      setSelectedIds([]);
      setBulkPriorityDialog({ open: false, value: "Medium" });
      fetchData();
    } catch {
      toast.error(t("priority_update_failed"));
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Edit item
  const openEdit = (item) =>
    setEditDialog({ open: true, item: { ...item }, loading: false });

  const handleEditSave = async () => {
    const { item } = editDialog;
    try {
      setEditDialog((d) => ({ ...d, loading: true }));
      await updateWishlistItem({
        Id: item.Id,
        ProductId: item.ProductId,
        Quantity: Number(item.Quantity),
        Priority: item.Priority,
        Notes: item.Notes || null,
        SupplierId: item.SupplierId || null,
      });
      toast.success(t("wishlist_item_updated"));
      setEditDialog({ open: false, item: null, loading: false });
      fetchData();
    } catch {
      toast.error(t("failed_to_update_wishlist_item"));
      setEditDialog((d) => ({ ...d, loading: false }));
    }
  };

  // Render
  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1,
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <FavoriteIcon sx={{ fontSize: 32, color: "error.main" }} />
          <Typography variant="h4" sx={{ color: "primary.main", fontWeight: 600 }}>
            {t("wishlist")}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          {someSelected && (
            <>
              <Button
                variant="outlined"
                color="warning"
                size="small"
                onClick={() => setBulkPriorityDialog({ open: true, value: "Medium" })}
                disabled={bulkActionLoading}
              >
                {t("update_priority")} ({selectedIds.length})
              </Button>
              <Button
                variant="outlined"
                color="error"
                size="small"
                startIcon={
                  bulkActionLoading ? <CircularProgress size={14} /> : <Delete fontSize="small" />
                }
                onClick={handleBulkDelete}
                disabled={bulkActionLoading}
              >
                {t("delete")} ({selectedIds.length})
              </Button>
            </>
          )}
          <Tooltip title={t("refresh")}>
            <IconButton onClick={fetchData}>
              <Refresh />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            color="primary"
            startIcon={<FavoriteIcon />}
            onClick={() => setAddDialog({ open: true })}
          >
            {t("add_item")}
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={pdfGenerating ? <CircularProgress size={16} /> : <PictureAsPdf />}
            onClick={handleExportPdf}
            disabled={filteredItems.length === 0 || pdfGenerating}
          >
            {pdfGenerating ? t("generating_pdf") : t("export_pdf")}
          </Button>
          {/* <Button
            variant="contained"
            startIcon={<ShoppingCart />}
            disabled={items.length === 0}
          >
            {t("order_all")}
          </Button> */}
          <Button
            variant="outlined"
            color="error"
            startIcon={<Delete />}
            onClick={() => setClearListDialog(true)}
            disabled={items.length === 0}
          >
            {t("clear_list")}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Summary cards */}
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
        <StatCard label={t("total_items")} value={summary?.TotalItems ?? items.length} color="primary.main" />
        <StatCard
          label={t("estimated_value")}
          value={`€${(summary?.TotalEstimatedValue ?? 0).toFixed(2)}`}
          color="success.main"
        />
        <StatCard label={t("high_priority")} value={summary?.HighPriorityItems ?? 0} color="error.main" />
        <StatCard label={t("in_stock")} value={summary?.InStockItems ?? 0} color="success.main" />
        <StatCard label={t("out_of_stock")} value={summary?.OutOfStockItems ?? 0} color="warning.main" />
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
          <TextField
            size="small"
            placeholder={t("search_wishlist_placeholder")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 220 }}
          />

          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <FilterList fontSize="small" color="action" />
            {["All", "High", "Medium", "Low"].map((p) => (
              <Chip
                key={p}
                label={p}
                size="small"
                color={priorityFilter === p ? PRIORITY_COLOR[p] ?? "primary" : "default"}
                variant={priorityFilter === p ? "filled" : "outlined"}
                onClick={() => setPriorityFilter(p)}
                sx={{ cursor: "pointer" }}
              />
            ))}
          </Box>

          {suppliers.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>{t("supplier")}</InputLabel>
              <Select
                value={supplierFilter}
                label={t("supplier")}
                onChange={(e) => setSupplierFilter(e.target.value)}
              >
                <MenuItem value="">
                  <em>{t("all_suppliers")}</em>
                </MenuItem>
                {suppliers.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>
      </Paper>

      {/* Table */}
      <Paper elevation={3}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "grey.100" }}>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected && !allSelected}
                    onChange={toggleAll}
                  />
                </TableCell>
                <TableCell sx={{ fontWeight: 700, width: 56 }}>{t("image")}</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{t("product")}</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{t("sku")}</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{t("supplier")}</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">
                  {t("qty")}
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">
                  {t("estimated_price")}
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">
                  {t("total")}
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">
                  {t("priority")}
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">
                  {t("stock")}
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{t("date_added")}</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">
                  {t("actions")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredItems.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={12} align="center" sx={{ py: 5 }}>
                    <Typography color="text.secondary">
                      {items.length === 0
                        ? t("no_items_wishlist")
                        : t("no_matching_wishlist_items")}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredItems.map((item) => (
                  <TableRow
                    key={item.Id}
                    hover
                    selected={selectedIds.includes(item.Id)}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedIds.includes(item.Id)}
                        onChange={() => toggleOne(item.Id)}
                      />
                    </TableCell>
                    <TableCell sx={{ width: 56, p: 0.5 }}>
                      {item.ProductImageUrl ? (
                        <Box
                          component="img"
                          src={item.ProductImageUrl}
                          alt={getWishlistProductName(item)}
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                          sx={{
                            width: 48,
                            height: 48,
                            objectFit: 'cover',
                            borderRadius: 1,
                            display: 'block',
                            border: '1px solid',
                            borderColor: 'divider',
                          }}
                        />
                      ) : (
                        <Box sx={{ width: 48, height: 48, bgcolor: 'grey.100', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.6rem' }}>{t("no_image")}</Typography>
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {getWishlistProductName(item)}
                      </Typography>
                      {item.Notes && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {item.Notes}
                        </Typography>
                      )}
                      {item.ProductCategoryName && (
                        <Typography variant="caption" color="text.disabled">
                          {item.ProductCategoryName}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {item.ProductSKU || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{item.SupplierName || "-"}</Typography>
                    </TableCell>
                    <TableCell align="center">{item.Quantity}</TableCell>
                    <TableCell align="right">€{(item.EstimatedPrice || 0).toFixed(2)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      €{(item.TotalEstimatedValue || 0).toFixed(2)}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={item.Priority}
                        size="small"
                        color={PRIORITY_COLOR[item.Priority] ?? "default"}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={item.IsInStock ? item.CurrentStock : "Out"}
                        size="small"
                        color={item.IsInStock ? "success" : "error"}
                        variant={item.IsInStock ? "filled" : "outlined"}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {item.DateAdded
                          ? new Date(item.DateAdded).toLocaleDateString()
                          : "-"}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={t("edit")}>
                        <IconButton size="small" color="primary" onClick={() => openEdit(item)}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t("delete")}>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setDeleteDialog({ open: true, item })}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {filteredItems.length > 0 && (
          <Box
            sx={{
              p: 2,
              display: "flex",
              justifyContent: "flex-end",
              borderTop: "1px solid",
              borderColor: "divider",
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {filteredItems.length} of {items.length} items &nbsp;|&nbsp;
              <strong>Total: €{filteredTotal.toFixed(2)}</strong>
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Delete confirmation */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, item: null })}
      >
        <DialogTitle>{t("confirm_delete")}</DialogTitle>
        <DialogContent>
          <Typography>
            {t("remove_wishlist_item_confirmation", { item: getWishlistProductName(deleteDialog.item) })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, item: null })}>
            {t("cancel")}
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            {t("delete") || "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit dialog */}
      <Dialog
        open={editDialog.open}
        onClose={() => setEditDialog({ open: false, item: null, loading: false })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t("edit_wishlist_item")}</DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          {editDialog.item && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                {getWishlistProductName(editDialog.item)}
              </Typography>
              <TextField
                fullWidth
                label={t("quantity")}
                type="number"
                value={editDialog.item.Quantity}
                onChange={(e) =>
                  setEditDialog((d) => ({
                    ...d,
                    item: { ...d.item, Quantity: Math.max(1, Number(e.target.value)) },
                  }))
                }
                inputProps={{ min: 1 }}
              />
              <FormControl fullWidth>
                <InputLabel>{t("priority")}</InputLabel>
                <Select
                  value={editDialog.item.Priority || "Medium"}
                  label={t("priority")}
                  onChange={(e) =>
                    setEditDialog((d) => ({
                      ...d,
                      item: { ...d.item, Priority: e.target.value },
                    }))
                  }
                >
                  <MenuItem value="Low">{t("low") || "Low"}</MenuItem>
                  <MenuItem value="Medium">{t("medium") || "Medium"}</MenuItem>
                  <MenuItem value="High">{t("high") || "High"}</MenuItem>
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label={t("notes_optional") || "Notes (optional)"}
                multiline
                rows={3}
                value={editDialog.item.Notes || ""}
                onChange={(e) =>
                  setEditDialog((d) => ({
                    ...d,
                    item: { ...d.item, Notes: e.target.value },
                  }))
                }
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setEditDialog({ open: false, item: null, loading: false })}
            disabled={editDialog.loading}
          >
            {t("cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleEditSave}
            disabled={editDialog.loading}
            startIcon={editDialog.loading ? <CircularProgress size={16} /> : null}
          >
            {t("save") || "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk priority update dialog */}
      <Dialog
        open={bulkPriorityDialog.open}
        onClose={() => setBulkPriorityDialog((d) => ({ ...d, open: false }))}
      >
        <DialogTitle>
          {t("update_priority")} - {selectedIds.length}{" "}
          {t("items")}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <FormControl fullWidth>
            <InputLabel>{t("new_priority")}</InputLabel>
            <Select
              value={bulkPriorityDialog.value}
              label={t("new_priority")}
              onChange={(e) =>
                setBulkPriorityDialog((d) => ({ ...d, value: e.target.value }))
              }
            >
              <MenuItem value="Low">{t("low") || "Low"}</MenuItem>
              <MenuItem value="Medium">{t("medium") || "Medium"}</MenuItem>
              <MenuItem value="High">{t("high") || "High"}</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkPriorityDialog((d) => ({ ...d, open: false }))}>
            {t("cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleBulkUpdatePriority}
            disabled={bulkActionLoading}
            startIcon={bulkActionLoading ? <CircularProgress size={16} /> : null}
          >
            {t("update") || "Update"}
          </Button>
        </DialogActions>
      </Dialog>


      {/* Confirmation Dialog for PDF Export */}
      <Dialog
        open={confirmPdfDialog}
        onClose={() => setConfirmPdfDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          bgcolor: "#581c87", 
          color: "white",
          display: "flex",
          alignItems: "center",
          gap: 1
        }}>
          <PictureAsPdf />
          {t("confirm_export") || "Confirm PDF Export"}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body1" gutterBottom>
              {t("export_confirmation_message") || "You are about to export a purchase order PDF with the following details:"}
            </Typography>
          </Box>
          
          <Box sx={{ pl: 2, mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>{t("store") || "Store"}:</strong> {companyInfo?.CompanyName || companyInfo?.Name || "Not set"}
            </Typography>
            {supplierFilter && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>{t("supplier") || "Supplier"}:</strong> {suppliers.find((s) => s.id === Number(supplierFilter))?.name || "-"}
              </Typography>
            )}
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>{t("total_items") || "Total Items"}:</strong> {filteredItems.length}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>{t("columns_included") || "Columns Included"}:</strong> Product Name, Category, Barcode, SKU, Quantity, Priority
            </Typography>
          </Box>
          
          <Alert severity="info" sx={{ mt: 2 }}>
            {t("pdf_features_note") || "PDF includes multiline product names, highlighted store details, and premium table design."}
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => setConfirmPdfDialog(false)}
            variant="outlined"
          >
            {t("cancel") || "Cancel"}
          </Button>
          <Button
            onClick={() => {
              setConfirmPdfDialog(false);
              generatePdf();
            }}
            variant="contained"
            startIcon={<PictureAsPdf />}
            disabled={pdfGenerating}
            sx={{ 
              bgcolor: "#581c87",
              "&:hover": { bgcolor: "#6b21a8" }
            }}
          >
            {t("export_pdf") || "Export PDF"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clear List Confirmation Dialog */}
      <Dialog
        open={clearListDialog}
        onClose={() => setClearListDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ 
          display: "flex", 
          alignItems: "center", 
          gap: 1,
          color: "error.main" 
        }}>
          <Delete />
          {t("clear_list") || "Clear List"}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            {t("clear_list_confirmation") || "Are you sure you want to clear the entire wishlist?"}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t("clear_list_warning", { count: items.length }) || `This will permanently delete all ${items.length} items. This action cannot be undone.`}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => setClearListDialog(false)}
            variant="outlined"
          >
            {t("cancel") || "Cancel"}
          </Button>
          <Button
            onClick={handleClearList}
            variant="contained"
            color="error"
            startIcon={bulkActionLoading ? <CircularProgress size={18} /> : <Delete />}
            disabled={bulkActionLoading}
          >
            {bulkActionLoading ? (t("clearing") || "Clearing...") : (t("clear_all") || "Clear All")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add to wishlist dialog (for manual adds) */}
      <AddToWishlistDialog
        open={addDialog.open}
        onClose={() => setAddDialog({ open: false })}
        product={null}
        onSuccess={fetchData}
      />
    </Box>
  );
};

export default WishlistPage;


